<template>
  <div class="scio-topbar-menu-anchor relative flex h-10 items-center overflow-visible" ref="dropdownContainer">
    <button
      ref="comboboxButton"
      type="button"
      :disabled="isDisabled"
      @click="toggleDropdown"
      class="group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-transparent bg-scio-yellow-p3/30 transition hover:bg-scio-yellow-p3/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-scio-yellow-p3 disabled:cursor-not-allowed disabled:opacity-50"
      :aria-expanded="showDropdown ? 'true' : 'false'"
      aria-haspopup="menu"
    >
      <span class="scio-text-body scio-weight-semibold leading-none text-slate-900">
        {{ userInitials }}
      </span>
    </button>

    <div
      v-if="showDropdown && !isDisabled"
      ref="dropdownBox"
      class="absolute right-0 top-12 z-[10000] w-[320px] overflow-hidden border border-neutral-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.12)]"
      role="menu"
    >
      <div class="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
        <div class="flex h-10 w-10 items-center justify-center bg-scio-yellow-p3/30 text-slate-900">
          <span class="scio-text-body scio-weight-heading">{{ userInitials }}</span>
        </div>
        <div class="min-w-0">
          <div class="truncate scio-text-body scio-weight-semibold text-slate-900">
            {{ userName || "—" }}
          </div>
          <div class="truncate scio-text-meta text-slate-500">
            {{ userEmail || "" }}
          </div>
        </div>
      </div>

      <div class="p-2">
        <button
          type="button"
          class="flex w-full items-center gap-3 border-l-2 border-transparent px-3 py-2 text-left scio-text-body text-slate-800 transition hover:border-scio-yellow-p3 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-scio-yellow-p3"
          @click="handleMenuItemClick('profile')"
          role="menuitem"
        >
          <img src="/icons/scio-people-01.svg" alt="" class="h-4 w-4 opacity-80" />
          <span>User profile</span>
        </button>

        <button
          v-if="isAdmin"
          type="button"
          class="flex w-full items-center gap-3 border-l-2 border-transparent px-3 py-2 text-left scio-text-body text-slate-800 transition hover:border-scio-yellow-p3 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-scio-yellow-p3"
          @click="handleMenuItemClick('admin')"
          role="menuitem"
        >
          <img src="/icons/scio-settings-01.svg" alt="" class="h-4 w-4 opacity-80" />
          <span>Admin</span>
        </button>

        <button
          type="button"
          class="flex w-full items-center gap-3 border-l-2 border-transparent px-3 py-2 text-left scio-text-body text-slate-800 transition hover:border-scio-yellow-p3 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-scio-yellow-p3"
          role="menuitem"
          title="Documentation"
          @click="handleMenuItemClick('documentation')"
        >
          <img src="/icons/scio-activity-01.svg" alt="" class="h-4 w-4 opacity-60" />
          <span>Documentation</span>
        </button>

        <button
          type="button"
          class="flex w-full items-center gap-3 border-l-2 border-transparent px-3 py-2 text-left scio-text-body text-slate-400"
          disabled
          role="menuitem"
          title="Coming soon"
        >
          <img src="/icons/scio-question-circle-01.svg" alt="" class="h-4 w-4 opacity-60" />
          <span>Support / FAQ</span>
        </button>

        <div class="my-2 h-px bg-slate-100"></div>

        <button
          type="button"
          class="flex w-full items-center gap-3 border-l-2 border-transparent px-3 py-2 text-left scio-text-body text-red-700 transition hover:border-red-400 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus:ring-red-300"
          @click="handleMenuItemClick('logout')"
          role="menuitem"
        >
          <img src="/icons/scio-logout.svg" alt="" class="h-4 w-4 opacity-80" />
          <span>Log out</span>
        </button>
      </div>
    </div>

    <modal-box
      v-if="showLogoutModal"
      header="Already leaving?"
      information="Your session will end and you will return to the Scio sign-in screen."
      icon="/icons/scio-logout.svg"
      :button-config="{
        customButtons: [
          { text: 'Stay signed in', value: 'cancel' },
          { text: 'Log out', value: 'logout' }
        ]
      }"
      @modal-click="handleLogoutModalClick"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import ModalBox from "@/common/components/ui/ModalBox.vue";
import { setCookie } from "@/utils/cookies";
import { fetchMe } from "@/api/user.api";

export default {
  name: "CurrentUser",
  components: { ModalBox },

  props: {
    isDisabled: { type: Boolean, default: false },
  },

  data() {
    return {
      showDropdown: false,
      showLogoutModal: false,
      me: null,
      isMeLoading: false,
    };
  },

  computed: {
    userEmail() {
      return this.me?.email || "";
    },

    userName() {
      const first = (this.me?.firstName || "").trim();
      const last = (this.me?.lastName || "").trim();
      const full = `${first} ${last}`.trim();
      return full || this.me?.displayName || "";
    },

    userInitials() {
      return this.me?.initials || "—";
    },

    isAdmin() {
      return String(this.me?.globalRoleCode || this.me?.globalRole || "").toUpperCase() === "ADMIN";
    },
  },

  methods: {
    ...mapActions("auth", ["logout"]),

    toggleDropdown() {
      if (!this.isDisabled) this.showDropdown = !this.showDropdown;
    },

    handleClickOutside(event) {
      if (this.$refs.dropdownContainer && !this.$refs.dropdownContainer.contains(event.target)) {
        this.showDropdown = false;
      }
    },

    handleMenuItemClick(action) {
      this.showDropdown = false;

      if (action === "admin" && !this.isAdmin) return;

      if (action === "logout") {
        this.showLogoutModal = true;
        return;
      }

      const routeByAction = {
        profile: { name: "user-profile" },
        admin: { name: "admin" },
        documentation: { name: "documentation" },
      };

      const target = routeByAction[action];
      if (!target) return;

      const resolved = this.$router.resolve(target);
      if (!resolved?.matched?.length) {
        console.warn("[CurrentUser] Route not registered", target);
        return;
      }

      this.$router.push(target);
    },

    async handleLogoutModalClick(event) {
      this.showLogoutModal = false;

      if (event.buttonType === "logout") {
        await this.logoutHardRedirect();
      }
    },

    async logoutHardRedirect() {
      try {
        await this.logout();
      } catch {
        // best effort
      } finally {
        setCookie("selectedTab", "login", 1);
        this.$router.push({ name: "home" });
      }
    },

    async loadMe() {
      if (this.isMeLoading) return;
      this.isMeLoading = true;

      try {
        const me = await fetchMe();

        if (!me?.isAuthenticated) {
          // /me is anonymous by design. Do not call the protected logout
          // endpoint when there is no server session to terminate.
          await this.logout({ skipServer: true });
          return;
        }

        this.me = me;
      } catch {
        await this.logoutHardRedirect();
      } finally {
        this.isMeLoading = false;
      }
    },
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.loadMe();
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>
